import React, { useMemo, useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { MousePointerClick, Monitor, Smartphone, Flame, RefreshCw, Info } from 'lucide-react';

/**
 * Banner Click Heatmap (Lab) — UYDURMA / DEMO amaçlı.
 * Bir e-ticaret ana sayfasındaki banner konumlarına göre tıklama yoğunluğunu
 * masaüstü (üst) ve mobil (alt) mockup üzerinde ısı haritası olarak gösterir.
 * Gerçek veri yok — konum bazlı sentetik sinyal. Sunum/konsept içindir.
 */

// Deterministik "rastgelelik" — her banner için sabit ama organik görünen sayılar.
const seedHash = (s) => {
  let h = 2166136261;
  for (let i = 0; i < s.length; i++) { h ^= s.charCodeAt(i); h = Math.imul(h, 16777619); }
  return (h >>> 0);
};

// blue → green → amber → red ısı skalası
const heatColor = (t, alpha = 1) => {
  const x = Math.max(0, Math.min(1, t));
  const stops = [[0, [37, 99, 235]], [0.4, [16, 185, 129]], [0.7, [245, 158, 11]], [1, [239, 68, 68]]];
  for (let i = 1; i < stops.length; i++) {
    if (x <= stops[i][0]) {
      const [t0, c0] = stops[i - 1], [t1, c1] = stops[i];
      const f = (x - t0) / (t1 - t0);
      const c = c0.map((v, j) => Math.round(v + (c1[j] - v) * f));
      return `rgba(${c[0]},${c[1]},${c[2]},${alpha})`;
    }
  }
  return `rgba(239,68,68,${alpha})`;
};

// Banner konumu: yüzdesel (kapsayıcıya göre) — {top,left,w,h} + hot: ısı odağı
const mk = (id, label, size, pos, hot, weight) => {
  const h = seedHash(id);
  const impressions = 8000 + (h % 42000) + Math.round(weight * 60000);
  const ctrBase = 0.4 + (weight * 4.2) + ((h % 60) / 100); // %
  const clicks = Math.round(impressions * (ctrBase / 100));
  return { id, label, size, pos, hot, impressions, clicks, ctr: (clicks / impressions) * 100 };
};

const DESKTOP = [
  mk('d-leader', 'Leaderboard', '970×90', { top: 4, left: 6, w: 88, h: 7 }, { x: 32, y: 50 }, 0.55),
  mk('d-hero', 'Hero Banner', '1200×420', { top: 14, left: 6, w: 58, h: 34 }, { x: 38, y: 58 }, 1.0),
  mk('d-side-a', 'Sidebar A', '300×250', { top: 14, left: 67, w: 27, h: 16 }, { x: 50, y: 45 }, 0.62),
  mk('d-side-b', 'Sidebar B', '300×250', { top: 32, left: 67, w: 27, h: 16 }, { x: 50, y: 50 }, 0.30),
  mk('d-tile-1', 'Promo 1', '380×200', { top: 52, left: 6, w: 27, h: 14 }, { x: 50, y: 55 }, 0.48),
  mk('d-tile-2', 'Promo 2', '380×200', { top: 52, left: 36.5, w: 27, h: 14 }, { x: 50, y: 50 }, 0.34),
  mk('d-tile-3', 'Promo 3', '380×200', { top: 52, left: 67, w: 27, h: 14 }, { x: 50, y: 45 }, 0.22),
  mk('d-mid', 'In-Content', '970×120', { top: 70, left: 6, w: 88, h: 10 }, { x: 60, y: 50 }, 0.40),
  mk('d-footer', 'Footer Banner', '970×90', { top: 85, left: 6, w: 88, h: 8 }, { x: 45, y: 50 }, 0.14),
];

const MOBILE = [
  mk('m-sticky-top', 'Sticky Top', '320×50', { top: 2.5, left: 5, w: 90, h: 5 }, { x: 50, y: 50 }, 0.42),
  mk('m-hero', 'Hero', '360×300', { top: 9.5, left: 5, w: 90, h: 20 }, { x: 45, y: 60 }, 1.0),
  mk('m-inline-1', 'Inline 1', '360×160', { top: 32, left: 5, w: 90, h: 13 }, { x: 55, y: 50 }, 0.66),
  mk('m-inline-2', 'Inline 2', '360×160', { top: 47.5, left: 5, w: 90, h: 13 }, { x: 45, y: 50 }, 0.40),
  mk('m-inline-3', 'Inline 3', '360×160', { top: 63, left: 5, w: 90, h: 13 }, { x: 50, y: 50 }, 0.20),
  mk('m-sticky-bot', 'Sticky Bottom', '360×60', { top: 92, left: 5, w: 90, h: 6 }, { x: 50, y: 50 }, 0.58),
];

const fmt = (n) => Math.round(n).toLocaleString('tr-TR');

function Zone({ z, metric, max, hovered, onHover }) {
  const val = metric === 'ctr' ? z.ctr : z.clicks;
  const t = max > 0 ? val / max : 0;
  const isHot = hovered === z.id;
  return (
    <div
      onMouseEnter={() => onHover(z.id)}
      onMouseLeave={() => onHover(null)}
      className="absolute rounded-lg border transition-all duration-200 cursor-pointer overflow-hidden"
      style={{
        top: `${z.pos.top}%`, left: `${z.pos.left}%`, width: `${z.pos.w}%`, height: `${z.pos.h}%`,
        borderColor: heatColor(t, 0.9),
        boxShadow: isHot ? `0 0 0 2px ${heatColor(t, 0.9)}, 0 8px 24px ${heatColor(t, 0.35)}` : 'none',
        background: `radial-gradient(circle at ${z.hot.x}% ${z.hot.y}%, ${heatColor(t, 0.85)} 0%, ${heatColor(t, 0.45)} 38%, ${heatColor(t, 0.12)} 70%, rgba(255,255,255,0.02) 100%)`,
        zIndex: isHot ? 20 : 1,
      }}
    >
      <div className="absolute inset-0 flex flex-col items-center justify-center px-1 text-center">
        <span className="text-[10px] sm:text-[11px] font-semibold text-white/95 drop-shadow leading-tight truncate max-w-full">{z.label}</span>
        <span className="text-[10px] sm:text-xs font-bold text-white drop-shadow tabular-nums">
          {metric === 'ctr' ? `${z.ctr.toFixed(2)}%` : fmt(z.clicks)}
        </span>
      </div>
    </div>
  );
}

function DeviceFrame({ kind, zones, metric, max, hovered, onHover }) {
  const isDesktop = kind === 'desktop';
  return (
    <div className={isDesktop ? 'w-full' : 'w-[300px] mx-auto'}>
      {/* chrome */}
      <div className={`bg-slate-800 ${isDesktop ? 'rounded-t-xl px-3 py-2' : 'rounded-t-[2rem] px-4 py-2.5'} flex items-center gap-2`}>
        {isDesktop ? (
          <>
            <span className="w-3 h-3 rounded-full bg-red-400" />
            <span className="w-3 h-3 rounded-full bg-amber-400" />
            <span className="w-3 h-3 rounded-full bg-green-400" />
            <div className="ml-3 flex-1 h-6 rounded-md bg-slate-700 flex items-center px-3">
              <span className="text-[11px] text-slate-300 truncate">https://demo.glowytics.com</span>
            </div>
          </>
        ) : (
          <div className="mx-auto w-24 h-4 rounded-full bg-slate-700" />
        )}
      </div>
      {/* viewport */}
      <div
        className={`relative bg-slate-50 border-x border-b border-slate-800 ${isDesktop ? 'rounded-b-xl' : 'rounded-b-[2rem]'}`}
        style={{ aspectRatio: isDesktop ? '16 / 10' : '9 / 19' }}
      >
        {/* faint page skeleton */}
        <div className="absolute inset-0 opacity-[0.5] pointer-events-none">
          <div className="h-full w-full bg-[linear-gradient(0deg,#eef2f7_1px,transparent_1px),linear-gradient(90deg,#eef2f7_1px,transparent_1px)] bg-[size:24px_24px]" />
        </div>
        {zones.map((z) => (
          <Zone key={z.id} z={z} metric={metric} max={max} hovered={hovered} onHover={onHover} />
        ))}
      </div>
      <div className="flex items-center justify-center gap-1.5 mt-2 text-slate-500">
        {isDesktop ? <Monitor className="w-3.5 h-3.5" /> : <Smartphone className="w-3.5 h-3.5" />}
        <span className="text-xs font-medium">{isDesktop ? 'Masaüstü' : 'Mobil'}</span>
      </div>
    </div>
  );
}

export default function BannerClickHeatmap() {
  const { selectedBrand } = useAuth();
  const [metric, setMetric] = useState('clicks'); // 'clicks' | 'ctr'
  const [hovered, setHovered] = useState(null);

  const all = useMemo(() => [...DESKTOP, ...MOBILE], []);
  const max = useMemo(() => Math.max(...all.map((z) => (metric === 'ctr' ? z.ctr : z.clicks))), [all, metric]);
  const totalClicks = useMemo(() => all.reduce((s, z) => s + z.clicks, 0), [all]);
  const ranked = useMemo(
    () => [...all].sort((a, b) => (metric === 'ctr' ? b.ctr - a.ctr : b.clicks - a.clicks)).slice(0, 6),
    [all, metric]
  );
  const hoveredZone = all.find((z) => z.id === hovered);

  return (
    <div className="p-6 max-w-6xl mx-auto space-y-6">
      {/* Header */}
      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="p-2.5 bg-gradient-to-br from-orange-500 to-red-500 rounded-xl shadow-sm">
              <Flame className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-slate-900">Banner Click Heatmap</h1>
              <p className="text-sm text-slate-500 mt-0.5">
                {selectedBrand?.name || 'Demo Store'} · Banner konumuna göre tıklama yoğunluğu · masaüstü + mobil
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <div className="flex items-center gap-1 bg-slate-100 p-1 rounded-lg">
              {[['clicks', 'Tıklama'], ['ctr', 'CTR']].map(([k, l]) => (
                <button key={k} onClick={() => setMetric(k)}
                  className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${metric === k ? 'bg-white shadow-sm text-slate-900' : 'text-slate-500 hover:text-slate-700'}`}>
                  {l}
                </button>
              ))}
            </div>
            <button onClick={() => setHovered(null)}
              className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50">
              <RefreshCw className="w-4 h-4" /> Sıfırla
            </button>
          </div>
        </div>

        {/* KPI row */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-5">
          {[
            { label: 'Toplam Tıklama', value: fmt(totalClicks), icon: MousePointerClick },
            { label: 'Takip Edilen Banner', value: all.length, icon: Flame },
            { label: 'En Sıcak Bölge', value: ranked[0]?.label || '—', icon: Flame },
            { label: 'En Yüksek CTR', value: `${Math.max(...all.map((z) => z.ctr)).toFixed(2)}%`, icon: MousePointerClick },
          ].map((k) => (
            <div key={k.label} className="rounded-xl bg-slate-50 border border-slate-100 p-3">
              <p className="text-[11px] uppercase tracking-wide text-slate-400 font-medium">{k.label}</p>
              <p className="text-lg font-bold text-slate-900 mt-0.5 truncate">{k.value}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Heatmaps */}
      <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-6">
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6 space-y-8">
          <DeviceFrame kind="desktop" zones={DESKTOP} metric={metric} max={max} hovered={hovered} onHover={setHovered} />
          <DeviceFrame kind="mobile" zones={MOBILE} metric={metric} max={max} hovered={hovered} onHover={setHovered} />

          {/* Legend */}
          <div className="flex items-center justify-center gap-3 pt-2">
            <span className="text-xs text-slate-500">Düşük</span>
            <div className="h-3 w-56 rounded-full" style={{ background: 'linear-gradient(90deg, rgba(37,99,235,0.9), rgba(16,185,129,0.9), rgba(245,158,11,0.9), rgba(239,68,68,0.9))' }} />
            <span className="text-xs text-slate-500">Yüksek</span>
          </div>
        </div>

        {/* Side panel */}
        <div className="space-y-4">
          {/* Hover detail */}
          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5 min-h-[132px]">
            {hoveredZone ? (
              <>
                <div className="flex items-center gap-2">
                  <span className="w-2.5 h-2.5 rounded-full" style={{ background: heatColor((metric === 'ctr' ? hoveredZone.ctr : hoveredZone.clicks) / max) }} />
                  <h3 className="font-semibold text-slate-900">{hoveredZone.label}</h3>
                  <span className="ml-auto text-[11px] text-slate-400 font-mono">{hoveredZone.size}</span>
                </div>
                <div className="grid grid-cols-3 gap-2 mt-3">
                  <div><p className="text-[10px] uppercase text-slate-400">Tıklama</p><p className="text-sm font-bold text-slate-900">{fmt(hoveredZone.clicks)}</p></div>
                  <div><p className="text-[10px] uppercase text-slate-400">Gösterim</p><p className="text-sm font-bold text-slate-900">{fmt(hoveredZone.impressions)}</p></div>
                  <div><p className="text-[10px] uppercase text-slate-400">CTR</p><p className="text-sm font-bold text-slate-900">{hoveredZone.ctr.toFixed(2)}%</p></div>
                </div>
              </>
            ) : (
              <div className="flex flex-col items-center justify-center h-full text-slate-400 gap-2">
                <Info className="w-5 h-5" />
                <p className="text-xs text-center">Detay için bir banner üzerine gel</p>
              </div>
            )}
          </div>

          {/* Ranking */}
          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5">
            <h3 className="font-semibold text-slate-900 mb-3 text-sm">En Çok Tıklanan Konumlar</h3>
            <div className="space-y-2">
              {ranked.map((z, i) => {
                const val = metric === 'ctr' ? z.ctr : z.clicks;
                const t = max > 0 ? val / max : 0;
                return (
                  <div key={z.id} className="flex items-center gap-3" onMouseEnter={() => setHovered(z.id)} onMouseLeave={() => setHovered(null)}>
                    <span className="text-xs font-bold text-slate-400 w-4">{i + 1}</span>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between mb-0.5">
                        <span className="text-xs font-medium text-slate-700 truncate">{z.label}</span>
                        <span className="text-xs font-bold text-slate-900 tabular-nums">{metric === 'ctr' ? `${z.ctr.toFixed(2)}%` : fmt(z.clicks)}</span>
                      </div>
                      <div className="h-1.5 rounded-full bg-slate-100 overflow-hidden">
                        <div className="h-full rounded-full" style={{ width: `${Math.max(t * 100, 4)}%`, background: heatColor(t, 0.95) }} />
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 flex items-start gap-2">
            <Info className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
            <p className="text-[11px] text-amber-800 leading-relaxed">
              Deneysel / demo. Tıklama verisi banner konumundan türetilmiş sentetik sinyaldir — gerçek pixel verisi değildir.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
